/**
 * 71. Time & Attendance — clock-in/out; timesheet approval; overtime.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate, formatQuantity } from "@/lib/money";

interface AttendanceRow {
  id: string;
  date: string;
  employee: string;
  clockIn: string | null;
  clockOut: string | null;
  hours: string;
  overtimeHours: string;
  status: "Pending" | "Approved" | "Rejected";
}

export function TimeAttendancePage() {
  const { data, isLoading } = useApiList<AttendanceRow>(
    ["hr", "attendance"],
    "/hr/attendance",
  );

  return (
    <div>
      <PageHeader
        title="Time & Attendance"
        subtitle="Clock-in/out, timesheet approval, and overtime."
        actions={<Button variant="primary">Export timesheet</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Attendance records (${data.total})`}>
          <DataTable<AttendanceRow>
            rows={data.items}
            columns={[
              { key: "date", label: "Date", render: (r) => formatDate(r.date) },
              { key: "employee", label: "Employee" },
              {
                key: "clockIn",
                label: "Clock in",
                render: (r) => r.clockIn ?? "—",
              },
              {
                key: "clockOut",
                label: "Clock out",
                render: (r) => r.clockOut ?? "—",
              },
              {
                key: "hours",
                label: "Hours",
                numeric: true,
                render: (r) => formatQuantity(r.hours, 2),
              },
              {
                key: "overtimeHours",
                label: "Overtime",
                numeric: true,
                render: (r) => formatQuantity(r.overtimeHours, 2),
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Approved" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Rejected" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : (
                    <Badge tone="warning">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
